import { css } from '@emotion/react';
import {
  faBurgerGlass, faCarBus,
  faCircleUser, faCircleUserCircleUser, faClothesHanger, faHandHoldingSeedling, faHouseBuilding, faStore, faSyringe,
  faUserPolice,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { useExportReport } from '~/exportReport/useExportReport';
import { type ModalProps } from '~/modal/modalType.enum';
import { modalContentStyle } from '~/proximity/edit/proximityEdit.styles';
import type { Translations } from '~/translations/translations.type';
import bdp from '../template/converted/bdp.rt.yml';
import crm from '../template/converted/crm.rt.yml';
import cxa from '../template/converted/cxa.rt.yml';
import cxf from '../template/converted/cxf.rt.yml';
import cxs from '../template/converted/cxs.rt.yml';
import cxt from '../template/converted/cxt.rt.yml';
import env from '../template/converted/env.rt.yml';
import hp from '../template/converted/hp.rt.yml';
import xp1 from '../template/converted/xp1.rt.yml';
import xp5 from '../template/converted/xp5.rt.yml';

export type ExportReportTemplateSelectorModalProps = ModalProps;

type TemplateSelectorItem = {
  id: string;
  template: ReportTemplate;
  icon: IconDefinition;
  title: keyof Translations;
  description: keyof Translations;
};

const items: TemplateSelectorItem[] = [{
  id: 'key-demographics',
  template: xp1,
  icon: faCircleUser,
  title: 'Key Demographics',
  description: 'Top-level demographic snapshot, current year only.',
}, {
  id: 'demographic-essentials',
  template: xp5,
  icon: faCircleUserCircleUser,
  title: 'Demographic Essentials',
  description: 'Full demographic breakdown including trends and projections.',
}, {
  id: 'crime-risk',
  template: crm,
  icon: faUserPolice,
  title: 'Crime Risk',
  description: 'Personal and property crime risk indices and grades.',
}, {
  id: 'health-profile',
  template: hp,
  icon: faSyringe,
  title: 'Health Profile',
  description: 'Demographics, vital statistics, and hospital data.',
}, {
  id: 'environmental-risk',
  template: env,
  icon: faHandHoldingSeedling,
  title: 'Environmental risk and topography',
  description: 'Climate, terrain, air quality, and natural hazard risk grades.',
}, {
  id: 'business-details-profile',
  template: bdp,
  icon: faStore,
  title: 'Business Detail Profile',
  description: 'Establishment and employee counts by industry.',
}, {
  id: 'cxs',
  template: cxs,
  icon: faHouseBuilding,
  title: 'Consumer Expenditures: Housing',
  description: 'Household shelter and utility spending with housing inventory.',
}, {
  id: 'cxf',
  template: cxf,
  icon: faBurgerGlass,
  title: 'Consumer Expenditures: Food and Grocery',
  description: 'Household spending on groceries, dining out, and drinks.',
}, {
  id: 'cxa',
  template: cxa,
  icon: faClothesHanger,
  title: 'Consumer Expenditures: Apparel',
  description: 'Household clothing and apparel spending by category.',
}, {
  id: 'cxt',
  template: cxt,
  icon: faCarBus,
  title: 'Consumer Expenditures: Transportation',
  description: 'Household transportation spending and commute data.',
}];

const contentWrapperStyle = css({
  padding: 24,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 16,
});

const itemWrapperStyle = (theme: Theme) => css({
  background: theme.backgroundColors.secondary,
  transition: 'background-color 0.2s',
  border: `1px solid ${theme.borderColors.primary}`,
  textAlign: 'left',
  padding: 12,
  borderRadius: 4,
  minHeight: 125,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  '&:hover': {
    background: theme.backgroundColors.secondaryHover,
  },
});

const iconStyle = css({
  fontSize: '24px',
  width: 'auto',
});

const titleStyle = css({
  fontSize: 16,
  fontWeight: 400,
  marginTop: 6,
});

const descriptionStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: 12,
  fontWeight: 400,
  marginTop: 2,
});

export const ExportReportTemplateSelectorModalComponent = ({ onClose, isOpen }: ExportReportTemplateSelectorModalProps) => {
  const [t] = useTranslation();
  const { selectReportTemplate } = useExportReport();

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      caption={t('Choose Summary report')}
      contentStyle={modalContentStyle}
      maxWidth={976}
    >
      <ScrollBarComponent type={ScrollbarType.Horizontal}>
        <div css={contentWrapperStyle}>
          {items.map(item => (
            <button
              css={itemWrapperStyle}
              key={item.id}
              onClick={() => selectReportTemplate(item.template)}
            >
              <div css={iconStyle}>
                <FontAwesomeIcon icon={item.icon} />
              </div>
              <div css={titleStyle}>
                {t(item.title)}
              </div>
              <div css={descriptionStyle}>
                {t(item.description)}
              </div>
            </button>
          ))}
        </div>
      </ScrollBarComponent>
    </ModalComponent>
  );
};
